/**
 * Bounded Context: Pyodide Bridge
 *
 * Wrapper TS sobre Pyodide WASM que carrega o kernel Python sob demanda.
 * Lazy load: Pyodide (~6MB) só é baixado quando o primeiro turn é processado.
 *
 * HLOS-13 — Endurecimento sobre o mock de HLOS-7:
 *  - Removido `any` (type safety ponta-a-ponta, sem @ts-ignore)
 *  - `ConstitutionalViolation` retornada pelo Python é detectada e traduzida
 *    para `ConstitutionalViolationError` (CC-01..08 / Princípio X)
 *  - Adicionada `processTurnStrict()` (Result<T, E>) para callers que preferem
 *    evitar try/catch
 *  - Re-exporta os data flows de `flows.ts`
 *
 * Constitucional:
 * - CL-07 (local-first): Pyodide roda 100% no browser; zero rede para dados sensíveis.
 * - CL-08 (ética fechada): bridge NUNCA envia θ_lat, memory, name, goals para fora.
 * - CL-09 (Explicabilidade): erros preservam contexto de qual CL foi violada.
 * - Princípio XI (Companion ≠ LLM): kernel Python é Companion, não LLM.
 *
 * Smoke test (CI Ubuntu sem Pyodide):
 * - `loadPyodide()` retorna mock quando `import.meta.env.MODE === 'test'` OU quando
 *   flag `VITE_USE_PYODIDE_MOCK=true`.
 * - `processTurn()` no modo mock retorna resposta determinística baseada no input.
 */

import {
  ConstitutionalViolationError,
  assertNoForbiddenBoundary,
  isKernelResponse,
  isProcessTurnResult,
  type KernelResponse,
  type ProcessTurnResult,
  type ProcessTurnStrictResult,
  type Result,
  type UserTurn,
} from "./types";
import { installWebLLMRuntime } from "./webllm_runtime";
import { injectAppleLLMRuntime } from "../capacitor/apple_llm_bridge";
import { injectAppleBridges } from "../capacitor/apple_bridges";
import { isApplePlatform } from "../capacitor/vault_bridge";

// ─────────────────────────────────────────────────────────────────────────
// HLOS-169 — Observability helpers
// ─────────────────────────────────────────────────────────────────────────

/**
 * HLOS-169: gera um correlation ID curto (8 hex chars) por turno.
 *
 * Uso: thread-local no bridge, propagado para o fallback. Logado
 * cross-stack (TS + Python) para permitir debug de turnos falhos.
 *
 * Conformidade:
 * - Princípio X (Dignidade): log só em `console.error` para dev, learner não vê.
 * - CL-09 (Explicabilidade): correlation_id logado junto com kind do erro.
 * - Princípio XI (Companion ≠ LLM): não vaza nome de provedor/modelo.
 */
function genCorrelationId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 8);
  }
  // Fallback determinístico (Node 16-, jsdom): Math.random hex.
  return Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, "0");
}

/**
 * HLOS-169: lista de keywords proibidas em logs (Princípio XI + CL-08).
 *
 * Garante que logs estruturados em `console.error` não vaze:
 * - Nomes de LLM/provedor (OpenAI, Anthropic, GPT, Claude, Gemini, ChatGPT)
 * - PII potencial (theta_lat, memory, learner_name, etc)
 * - masterKey (chave em plaintext — CL-08)
 *
 * `safeLogError()` remove essas keywords do error_message antes de logar.
 */
const FORBIDDEN_LOG_KEYWORDS = [
  "openai",
  "anthropic",
  "gpt-",
  "gpt4",
  "gpt-4",
  "gpt-3",
  "claude",
  "gemini",
  "chatgpt",
  "llm",
  "masterkey",
  "master_key",
  "theta_lat",
  "θ_lat",
  "memory_store",
  "goal_store",
  "learner_id",
  "learner_name",
];

/**
 * HLOS-169: log estruturado para erros técnicos. R3 silencioso para o
 * learner (mantém Princípio X — Dignidade), mas `console.error` para o dev.
 *
 * Estrutura do log:
 * ```
 * [HLOS] { correlation_id, kind, error_message_safe, stack_safe? }
 * ```
 *
 * Toggle: se `window.__hlos_debug__ === true`, inclui stack (dev only).
 * Default: stack OFF (produção, menor ruído no Console).
 */
function safeLogError(
  correlationId: string,
  kind: "vault_unavailable" | "pyodide_failed" | "kernel_threw" | "mock_fallback" | "boundary_violation",
  errorMessage: string,
  error?: unknown,
): void {
  let safeMessage = errorMessage;
  for (const keyword of FORBIDDEN_LOG_KEYWORDS) {
    // Case-insensitive replace; mantém length (zero-width) para dificultar fingerprinting.
    safeMessage = safeMessage.replace(new RegExp(keyword, "gi"), "\u200b");
  }
  // Remove também o próprio `masterKey` que pode vir do param
  const isDev =
    typeof window !== "undefined" && (window as { __hlos_debug__?: boolean }).__hlos_debug__ === true;
  const payload = {
    correlation_id: correlationId,
    kind,
    error_message_safe: safeMessage,
    ...(isDev && error instanceof Error ? { stack_safe: error.stack } : {}),
  };
  console.error("[HLOS]", payload);
}

/** Interface mínima que nossa bridge usa do Pyodide (duck-typing para test). */
export interface PyodideLike {
  runPythonAsync(code: string): Promise<unknown>;
  globals: { get(name: string): unknown; set(key: string, value: unknown): void };
}

/**
 * Interface crua do Pyodide real (`@pyodide/pyodide`). Carregada dinamicamente;
 * nunca importada estaticamente (Pyodide é dep opcional, bundle separado).
 *
 * Definida em `unknown` para evitar acoplar a versão do Pyodide no código
 * da bridge. Type guards runtime validam qualquer uso.
 */
export interface PyodideRaw {
  runPythonAsync(code: string): Promise<unknown>;
  globals: { get(name: string): unknown; set(key: string, value: unknown): void };
  loadPackage?(names: string[]): Promise<void>;
}

/** Estado interno: instância do Pyodide (lazy + cached). */
let pyodideInstance: PyodideLike | null = null;

/** HLOS-48: master key (hex) em memória, validada uma vez. */
let masterKeyHex: string | null = null;

/**
 * HLOS-48: injeta a master key (DEK hex) no bridge.
 *
 * Deve ser chamada UMA VEZ após vault unlock, ANTES da primeira mensagem.
 * Valida formato (64 chars hex = 32 bytes).
 *
 * @throws Error se hex inválido ou length errado.
 */
export function passMasterKeyToKernel(masterKeyHexValue: string): void {
  if (typeof masterKeyHexValue !== "string") {
    throw new Error("[HLOS Pyodide] masterKey deve ser hex string");
  }
  if (masterKeyHexValue.length !== 64) {
    throw new Error(
      `[HLOS Pyodide] masterKey deve ter 64 chars hex (32 bytes), recebido: ${masterKeyHexValue.length}`,
    );
  }
  if (!/^[0-9a-fA-F]{64}$/.test(masterKeyHexValue)) {
    throw new Error("[HLOS Pyodide] masterKey contém chars não-hex");
  }
  masterKeyHex = masterKeyHexValue;
  console.info(
    "[HLOS Pyodide] masterKey registered (64 chars hex). Note: NUNCA logada em plaintext.",
  );
}

/** HLOS-48: limpa master key (lock equivalente para o bridge). */
export function clearMasterKey(): void {
  masterKeyHex = null;
}

/**
 * HLOS-50 Sprint 5: Pyodide real agora funcional.
 *
 * Mudanças Sprint 5:
 * - `js_process_turn(turn_json: str) -> str` adicionado em hlos/kernel/bridge.py
 * - Exposto via hlos.kernel.__init__ para acesso direto
 * - MOCK_FORCED = false (Pyodide real é default)
 * - Fallback transparente para mock se QUALQUER parte do stack Python falhar
 *
 * v0.6.1: fallback mais robusto — se `import hlos.kernel` ou
 * `js_process_turn` falhar (wheel não carregou, runtime crash, etc),
 * silenciosamente usa mock em vez de mostrar "Companheiro não pôde ser
 * carregado". App nunca fica sem resposta.
 */
const MOCK_FORCED = false;
const RUNTIME_STRICT = false;

export function shouldUseMock(): boolean {
  if (MOCK_FORCED) return true;
  // HLOS-debug: forçar shouldUseMock = false em produção. O minifier Vite
  // está reescrevendo o check do import.meta de forma imprevisível,
  // e o mock está sendo chamado em produção (vimos `[MOCK timestamp]`
  // na resposta). Workaround: usar isProduction() como gate.
  if (isProduction()) return false;
  // Dev/test: usar lógica normal.
  const meta = (globalThis as { import?: { meta?: { env?: Record<string, string> } } }).import?.meta;
  if (meta && "env" in meta) {
    const env = meta.env;
    return Boolean(env) && (env!.MODE === "test" || env!.VITE_USE_PYODIDE_MOCK === "true");
  }
  const env = (globalThis as { process?: { env?: Record<string, string> } }).process?.env;
  return Boolean(env) && (env!.MODE === "test" || env!.VITE_USE_PYODIDE_MOCK === "true");
}

/**
 * HLOS-181: detecta se estamos rodando em produção (Vite build).
 *
 * Em produção (`import.meta.env.PROD === true`), o bridge NÃO usa mock
 * silencioso como fallback — qualquer falha do Pyodide é propagada como
 * exceção (Princípio X: Dignidade Cognitiva). Em dev/test, mock é
 * permitido para a suíte de testes funcionar sem Pyodide real.
 *
 * Returns:
 *   true se produção, false se dev/test.
 */
export function isProduction(): boolean {
  // Import.meta é preservado em ESM. O check typeof import.meta !== "undefined"
  // é minificado incorretamente pelo Vite/esbuild para "typeof import.meta<"u""
  // (comparação de string), o que sempre retorna true. Usar feature detection
  // direta via globalThis.import.meta para evitar o bug do minifier.
  const meta = (globalThis as { import?: { meta?: { env?: { PROD?: boolean; MODE?: string } } } }).import?.meta;
  if (meta && "env" in meta) {
    const env = meta.env;
    if (env?.PROD === true) return true;
    if (env?.MODE === "production") return true;
  }
  // 2. globalThis flag (override para tests/debug)
  const globalFlag = (globalThis as { __HLOS_PRODUCTION__?: boolean }).__HLOS_PRODUCTION__;
  if (globalFlag === true) return true;
  // 3. process.env (Node/build tools)
  if (typeof process !== "undefined" && process.env) {
    if (process.env.NODE_ENV === "production") return true;
    if (process.env.HLOS_PRODUCTION === "true" || process.env.HLOS_PRODUCTION === "1") {
      return true;
    }
  }
  // Default: assume dev (não-prod) para manter mock.
  return false;
}

/**
 * HLOS-181: erro lançado quando o kernel Pyodide está indisponível em
 * produção. NÃO é mock — é a falha real do runtime que precisa ser
 * comunicada ao caller.
 */
export class PyodideUnavailableError extends Error {
  override readonly name = "PyodideUnavailableError";
  constructor(message: string) {
    super(message);
  }
}

export function isStrict(): boolean {
  return RUNTIME_STRICT;
}

/**
 * URL do runtime Pyodide (WASM + stdlib).
 *
 * HLOS-178: default é o proxy `hlos-pyodide-proxy.workers.dev` (NÃO o CDN
 * jsdelivr direto). Justificativa: a CSP do PWA
 * (`apps/ui-shell/public/_headers:41`) só permite `connect-src 'self'
 * https://*.workers.dev` — jsdelivr está bloqueado. Sem o proxy,
 * `loadPyodide()` falha silenciosamente em prod, o `catch` ativa
 * `createMockPyodide()` e o learner recebe templates (HLOS-177).
 *
 * A whitelist `*.workers.dev` já cobre o proxy — sem mudança de CSP.
 *
 * Devs locais podem sobrescrever via `VITE_PYODIDE_INDEX_URL` (ver
 * `.env.example` na raiz). Em prod (vite build), o default do proxy é
 * usado a menos que a env var seja setada em runtime/build.
 *
 * CL-07 (local-first): o proxy é invisível ao learner; Pyodide roda
 * 100% client-side. O worker só serve binários WASM/JS estáticos.
 */
function pyodideIndexURL(): string {
  const env = (globalThis as { import?: { meta?: { env?: Record<string, string> } } })
    .import?.meta?.env;
  return (
    env?.VITE_PYODIDE_INDEX_URL ??
    "https://hlos-pyodide-proxy.jose-c-figueira.workers.dev/pyodide/v0.26.4/full/"
  );
}

/**
 * URL do wheel `hlos_kernel` (HLOS-12 deliverable) — pré-bundleado com o PWA.
 * Default: relativo ao origin atual (`/hlos_kernel-0.1.1-py3-none-any.whl`).
 * Pode ser sobrescrito por `VITE_HLOS_KERNEL_WHEEL_URL` (CDN, se necessário).
 *
 * NOTA: o wheel NÃO é bundled no bundle inicial (CL-07: bundle ≤ 50KB gzipped).
 * É baixado lazy quando a 1ª mensagem do learner é enviada.
 */
function hlosKernelWheelURL(): string {
  const env = (globalThis as { import?: { meta?: { env?: Record<string, string> } } })
    .import?.meta?.env;
  return env?.VITE_HLOS_KERNEL_WHEEL_URL ?? "/hlos_kernel-0.1.1-py3-none-any.whl";
}

/**
 * Inicializa o Pyodide (lazy + cached).
 * - Modo produção: baixa Pyodide WASM via CDN + carrega numpy/scipy + wheel `hlos_kernel`.
 * - Modo mock: retorna stub determinístico (para CI / test).
 *
 * Ordem de carga (HLOS-15):
 *   1. Pyodide runtime (CDN ou local)
 *   2. numpy + scipy (deps nativas do kernel)
 *   3. hlos_kernel wheel (HLOS-12 deliverable — REQ-003 §3.1)
 *
 * Após carregar o wheel, `js_process_turn` (registrada em Python via HLOS-12)
 * fica disponível para `runPythonAsync()`.
 */
export async function initPyodide(): Promise<PyodideLike> {
  if (pyodideInstance) return pyodideInstance;

  console.info("[HLOS Pyodide] initPyodide: shouldUseMock=", shouldUseMock());
  if (shouldUseMock()) {
    console.info("[HLOS Pyodide] using MOCK (no real Pyodide runtime)");
    pyodideInstance = createMockPyodide();
    return pyodideInstance;
  }

  // Tenta Pyodide real. Em falha (CDN bloqueado, WASM pesado), fallback para mock.
  try {
    console.info("[HLOS Pyodide] loading real Pyodide from", pyodideIndexURL());

    // 1. Lazy load: Pyodide runtime (~6MB) só é baixado quando necessário.
    // @pyodide/pyodide é dep optional — carregada via CDN em runtime.
    const pyodideModule = (await import(/* @vite-ignore */ "pyodide")) as {
      loadPyodide: (options: { indexURL: string }) => Promise<unknown>;
    };
    const raw = (await pyodideModule.loadPyodide({
      indexURL: pyodideIndexURL(),
    })) as PyodideRaw;
    pyodideInstance = raw;

    // HLOS-168-B.14: Setar WEBLLM_RUNTIME no ambiente do Pyodide ANTES
    // de criar o CompanionRuntime. Sem isso, _build_web_provider_factory()
    // retorna None (default "mock") e Python-side usa templates v1.
    // iOS nativo + Apple Intelligence disponível → "apple" (Foundation Models).
    // Web/Android → "web" (WebLLM mock).
    if (isApplePlatform()) {
      await raw.runPythonAsync(
        `import os; os.environ["WEBLLM_RUNTIME"] = "apple"`,
      );
      console.info("[HLOS Pyodide] WEBLLM_RUNTIME=apple (Foundation Models)");
    } else {
      await raw.runPythonAsync(
        `import os; os.environ["WEBLLM_RUNTIME"] = "web"`,
      );
      console.info("[HLOS Pyodide] WEBLLM_RUNTIME=web (WebLLM mock)");
    }

    // 2. Pré-carregar numpy/scipy (deps nativas do kernel).
    // HLOS-152: cryptography (deps do hlos.vault) — Pyodide inclui o pacote
    // mas precisa de loadPackage() explícito. Sem isso, hlos/vault/crypto.py
    // falha em `from cryptography.hazmat.backends import default_backend`.
    if (typeof raw.loadPackage === "function") {
      await raw.loadPackage(["numpy", "scipy", "cryptography"]);
    }

    // 2.5. HLOS-161: injetar `apple_llm_runtime` Pyodide global ANTES do
    // HLOS-323: instala TODAS as bridges iOS-specific no Pyodide.
    // - apple_llm_runtime: Foundation Models para o kernel Python
    // - vision_bridge: Apple Vision para análise de imagem
    const bridges = injectAppleBridges(raw);
    if (bridges.llm) {
      console.info("[HLOS Pyodide] apple_llm_runtime injected (iOS Capacitor)");
    }
    if (bridges.vision) {
      console.info("[HLOS Pyodide] vision_bridge injected (iOS Capacitor)");
    }
    // HLOS-318 backward compat: mantém chamada legada (no-op se já injetado)
    const appleInjected = injectAppleLLMRuntime(raw);
    if (appleInjected) {
      console.info("[HLOS Pyodide] apple_llm_runtime injected (iOS Capacitor)");
    }

    // HLOS-194: instalar `webllm_runtime` como global Pyodide ANTES do wheel.
    // Garante que o `WebLLMRuntimeMLC` Python-side (chamado pelo
    // `WebLocalProvider` quando o dispatcher roteia para local) encontre
    // o binding TS via `pyodide.globals.get("webllm_runtime")`.
    //
    // Single source of truth: substitui a chamada que vivia em
    // ConversationInit.vue (linhas 66-76, HLOS-90). Agora TODOS os call
    // sites de `initPyodide` (boot em ConversationInit + runtime via
    // useProcessTurn) recebem o runtime instalado — sem risco de o Python
    // chamar `bridge.globals.get("webllm_runtime")` e receber None.
    //
    // HLOS-332: instalar registry de turn-payload para bypassar o limite
    // de tamanho do runPythonAsync inline. Python recupera via
    // `import js; js.window.__hlos_bridge.pop_turn(turnId)`.
    (globalThis as unknown as Record<string, unknown>).__hlos_bridge = {
      pop_turn(turnId: string): unknown {
        const w = globalThis as unknown as Record<string, unknown>;
        // HLOS-339: ler de AMBOS pyodide.globals e globalThis (window)
        // para tolerar o sandbox do iOS WKWebView.
        const fromGlobalThis = w[turnId];
        // Tenta também pyodide.globals (caso a chave esteja lá)
        type WithPyodide = { pyodide?: { globals?: { get?: (k: string) => unknown; delete?: (k: string) => void } } };
        const fromPyodideGlobals = (w as WithPyodide).pyodide?.globals?.get?.(turnId);
        const turn = fromGlobalThis !== undefined ? fromGlobalThis : fromPyodideGlobals;
        delete w[turnId];
        if (fromPyodideGlobals !== undefined) {
          try {
            (w as WithPyodide).pyodide?.globals?.delete?.(turnId);
          } catch {
            // ignore
          }
        }
        console.info(`[HLOS-339:debug] pop_turn turnId=${turnId}; fromGlobalThis=${typeof fromGlobalThis}; fromPyodideGlobals=${typeof fromPyodideGlobals}; result_type=${turn === undefined ? "UNDEFINED" : typeof turn}`);
        return turn;
      },
    };

    // Idempotente (ver webllm_runtime.ts:installWebLLMRuntime).
    // Try-catch não-bloqueante: se falhar (bundle chunk não chegou, erro
    // de rede, etc), log warning e continua — CC-05 zero penalidade em
    // erro, templates v1 cobrem (HLOS-185..192).
    try {
      installWebLLMRuntime(raw as unknown as PyodideLike);
      console.info("[HLOS Pyodide] webllm_runtime installed (HLOS-194)");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn(
        `[HLOS Pyodide] webllm_runtime install failed (HLOS-194): ${msg}. ` +
          "LLM real ficará indisponível; templates v1 cobrem.",
      );
    }

    // 3. Carregar wheel hlos_kernel (HLOS-12 deliverable).
    // Idempotente: se já estiver carregado, loadPackage é no-op.
    // Estratégia: usar `loadPackage(<URL>)` (Pyodide aceita URL absoluta ou relativa).
    await loadHlosKernelWheel(raw);

    // HLOS-207: marca DOM dataset para o smoke E2E detectar Pyodide 100% pronto
    // (incluindo todos os loadPackage). Antes desse marker, `waitForResponse`
    // no smoke só capturava o fetch do `pyodide.asm.wasm`, perdendo o tempo de
    // download de numpy/scipy/cryptography/wheel (~40MB adicionais). CI sandbox
    // com throttling tinha timeout em 120s+90s mesmo após warm-up.
    // Marcador só é setado após initPyodide completar com sucesso — não em falha.
    if (typeof document !== "undefined") {
      document.documentElement.dataset["hlosPyodideReady"] = "true";
      document.documentElement.dataset["hlosPyodideFailed"] = "false";
      console.info("[HLOS Pyodide] DOM marker set (hlosPyodideReady=true)");
    }

    console.info("[HLOS Pyodide] real Pyodide ready");
    return pyodideInstance;
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    console.warn(
      `[HLOS Pyodide] real runtime failed: ${message}. ` +
      `Falling back to mock.`
    );
    if (isStrict()) throw e;
    // HLOS-181 hotfix: em produção, NÃO cair no mock silencioso
    // (Princípio X: Dignidade Cognitiva). Mock silencioso escondia
    // o problema do learner (eco "Anotado: ...").
    if (isProduction()) {
      // HLOS-207: marca DOM dataset para o smoke E2E detectar falha
      // (smoke espera por `hlosPyodideReady=true`; aqui `false` indica erro).
      if (typeof document !== "undefined") {
        document.documentElement.dataset["hlosPyodideReady"] = "false";
        document.documentElement.dataset["hlosPyodideFailed"] = "true";
        console.error(
          `[HLOS Pyodide] DOM marker set (hlosPyodideFailed=true): ${message}`,
        );
      }
      throw new PyodideUnavailableError(
        `Pyodide init failed in production: ${message}`,
      );
    }
    pyodideInstance = createMockPyodide();
    return pyodideInstance;
  }
}

/**
 * Carrega o wheel `hlos_kernel` no Pyodide runtime.
 * HLOS-15: chamado automaticamente em `initPyodide()` após numpy/scipy.
 *
 * Por que loadPackage (não micropip):
 *  - Mais simples (não requer import micropip)
 *  - Idempotente (chamar 2x é no-op)
 *  - Funciona com URL relativa (servido pelo mesmo origin do PWA)
 *
 * Se o wheel não estiver disponível (404 ou CORS), loga warning e continua.
 * Caller pode usar `runPythonAsync` mesmo sem o wheel — `js_process_turn` vai
 * falhar com PyodideError genérico (capturado pelo useProcessTurn).
 */
async function loadHlosKernelWheel(py: PyodideRaw): Promise<void> {
  if (typeof py.loadPackage !== "function") return;
  const wheelUrl = hlosKernelWheelURL();
  try {
    // Pyodide.loadPackage aceita string | string[]. Para wheel (.whl via URL),
    // passamos como array de um elemento.
    await py.loadPackage([wheelUrl]);
  } catch (e) {
    // Não-bloqueante: continua sem o wheel. Caller recebe erro técnico.
    // Em produção, isso seria visível ao usuário como "Pyodide runtime pronto,
    // mas kernel não foi carregado — verifique o build".
    console.warn(
      `[HLOS Pyodide] Falha ao carregar wheel ${wheelUrl}: ${String(e)}. ` +
        "Caller receberá erro técnico ao chamar process_turn.",
    );
  }
}

/**
 * Processa um turno via kernel Python.
 *
 * HLOS-211 (thread isolation): usa Web Worker dedicado para executar
 * processTurn em thread isolada. A UI thread permanece responsiva
 * (<100ms durante inferência LLM, AC-1).
 *
 * Caminho real (modo produção):
 *   1. Boundary check: garante que turn NÃO contém θ_lat, memory, learner_id, etc.
 *   2. (HLOS-211) Chama `processTurnInWorker()` que delega para Pyodide Worker.
 *   3. (Fallback) Se Worker indisponível, chama `processTurnStrict` in-thread.
 *   4. Valida resposta crua via `isKernelResponse` (type guard, sem `any`).
 *   5. Se `kind === "constitutional_violation"` → lança `ConstitutionalViolationError`.
 *   6. Se `kind === "ok"` → retorna `result`.
 *
 * @throws `ConstitutionalViolationError` se kernel detecta CC-01..08 violado.
 * @throws `Error` (genérico) se Pyodide falha ou payload corrompido.
 */
export async function processTurn(
  pyodide: PyodideLike,
  turn: UserTurn,
): Promise<ProcessTurnResult> {
  // HLOS-211: thread isolation via Worker dedicado
  try {
    const { processTurnInWorker } = await import("./thread-isolation");
    return await processTurnInWorker(turn);
  } catch (err) {
    // Fallback: se Worker não disponível (testes jsdom, etc.) ou falhou,
    // usa caminho in-thread legado.
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "[HLOS Pyodide] Worker não disponível, fallback para in-thread:",
        err,
      );
    }
    const result = await processTurnStrict(pyodide, turn);
    if (result.ok) return result.value;
    if (result.error.kind === "constitutional") {
      throw new ConstitutionalViolationError(result.error.violations);
    }
    throw new Error(result.error.message);
  }
}

/**
 * Versão strict de `processTurn` que retorna `Result<T, BridgeError>` em vez
 * de lançar. Útil para callers que preferem evitar try/catch.
 *
 * Vantagens:
 *  - Erros são valores, não exceptions (sem overhead de try/catch).
 *  - Caller decide o que fazer com violação constitucional (R3 silent? retry? UI?).
 *  - Compilador força o caller a tratar o caso de erro.
 *
 * Equivalente semântico a `processTurn`:
 *   - `Result.ok: true` ⇔ `processTurn` retornou normalmente
 *   - `Result.ok: false, error.kind: "constitutional"` ⇔ `processTurn` lançou ConstitutionalViolationError
 *   - `Result.ok: false, error.kind: "technical"` ⇔ `processTurn` lançou outro erro
 */
export async function processTurnStrict(
  pyodide: PyodideLike,
  turn: UserTurn,
): Promise<ProcessTurnStrictResult> {
  // HLOS-169: correlation ID por turno, para debug cross-stack (TS + Python).
  const correlationId = genCorrelationId();

  // 1. Boundary check (CL-07/CL-08): garante que turn não contém dados sensíveis.
  try {
    assertNoForbiddenBoundary(turn, "UserTurn");
  } catch (e) {
    // HLOS-169: erro constitucional (Princípio X) → silent R3, SEM console.error
    // (não é bug — é regra de negócio, logado em outro canal pelo Python).
    if (e instanceof ConstitutionalViolationError) {
      return {
        ok: false,
        error: {
          kind: "constitutional",
          violations: e.violations,
          cl_refs: ["CL-07", "CL-08"],
        },
      };
    }
    // Erro técnico (não-constitucional) no boundary check → log estruturado.
    safeLogError(
      correlationId,
      "boundary_violation",
      e instanceof Error ? e.message : String(e),
      e,
    );
    return {
      ok: false,
      error: { kind: "technical", message: e instanceof Error ? e.message : String(e) },
    };
  }

  // 2. Caminho real: chama o kernel Python via runPythonAsync.
  // HLOS-debug-2026-09-08: REMOVIDO fallback para mock silencioso em
  // produção. Se o runPythonAsync falhar, propaga a exceção. O mock
  // silencioso estava escondendo o problema real do learner.
  let rawResponse: unknown;
  try {
    rawResponse = await callKernel(pyodide, turn);
  } catch (e) {
    safeLogError(correlationId, "pyodide_failed", String(e), e);
    // HLOS-debug-2026-09-08: em PRODUÇÃO, sempre propaga o erro (Princípio X).
    // Em dev/test, ainda usa mock para compat com suíte.
    if (isProduction()) {
      throw new PyodideUnavailableError(
        `Kernel unavailable in production: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
    // Dev/test: mock silencioso (compat com suíte).
    rawResponse = mockKernelResponse(turn);
  }

  // 3. Valida tipo da resposta crua (sem `any`).
  if (!isKernelResponse(rawResponse)) {
    return {
      ok: false,
      error: {
        kind: "technical",
        message:
          "Kernel returned malformed response (not a valid KernelResponse JSON).",
      },
    };
  }
  const kernelResponse: KernelResponse = rawResponse;

  // 4. Despacha por kind.
  if (kernelResponse.kind === "constitutional_violation") {
    return {
      ok: false,
      error: {
        kind: "constitutional",
        violations: [...kernelResponse.violations],
        ...(kernelResponse.cl_refs
          ? { cl_refs: [...kernelResponse.cl_refs] }
          : {}),
      },
    };
  }
  // kind === "ok" — valida o result novamente por defesa em profundidade.
  if (!isProcessTurnResult(kernelResponse.result)) {
    return {
      ok: false,
      error: {
        kind: "technical",
        message: "KernelResponse.kind='ok' mas result não é ProcessTurnResult válido.",
      },
    };
  }
  return { ok: true, value: kernelResponse.result };
}

/**
 * Chama o kernel Python. Modo mock retorna resposta determinística.
 * Modo real serializa turn como JSON e chama `js_process_turn(turn_json)`.
 *
 * HLOS-181: em produção, qualquer falha do Pyodide é propagada como
 * `PyodideUnavailableError` (não mock silencioso). Em dev/test, mock
 * continua sendo o fallback explícito.
 */
async function callKernel(
  pyodide: PyodideLike,
  turn: UserTurn,
): Promise<unknown> {
  // HLOS-debug-2026-09-08: REMOVIDO if(shouldUseMock()) — o mock estava
  // sendo chamado em produção apesar do fix de isProduction(). Forçar
  // chamada do Pyodide real sempre (mesmo em dev/test, se Pyodide estiver
  // disponível). O caminho mockKernelResponse só deve ser alcançado se
  // runPythonAsync falhar completamente.
  // HLOS-331: log para debug — confirm que attachments está no payload
  console.info(
    "[HLOS-323:ts] turn keys=" + Object.keys(turn).join(",") +
    " attachments=" + (turn.attachments ? `${turn.attachments.length} (dataUrl len=${turn.attachments[0]?.dataUrl?.length ?? 0})` : "MISSING"),
  );
  // HLOS-332: pass turn via global (não inline). Pyodide tem limite de ~1MB
  // para runPythonAsync inline; com imagem 4MB o payload é dropado silenciosamente.
  // Salvar em global e passar ID para Python recuperar.
  const turnId = `__hlos_turn_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  // HLOS-339: set em AMBOS pyodide.globals E globalThis (window).
  // No iOS WKWebView, pyodide.globals e globalThis podem ser objetos
  // diferentes (cap詑a sandbox do WebView). Set em ambos para garantir.
  (pyodide as unknown as { globals: { set: (k: string, v: unknown) => void } }).globals.set(turnId, turn);
  (globalThis as unknown as Record<string, unknown>)[turnId] = turn;
  // HLOS-48: master key via param (NÃO via global, NÃO via arquivo).
  // Se não foi registrada, Python-side vai usar a passphrase determinística
  // local (modo legacy/mock-like para tests).
  const masterKeyArg = masterKeyHex ? `"${masterKeyHex}"` : '""';

  // HLOS-50 Sprint 5: js_process_turn vive em `hlos.kernel` (exposto via
  // `from .bridge import js_process_turn` em hlos/kernel/__init__.py).
  // HLOS-48: js_process_turn agora aceita 2º param (master_key_hex).
  //
  // HLOS-181: se QUALQUER parte falhar (wheel não carregou, import error,
  // js_process_turn throws), o comportamento depende do ambiente:
  // - Produção: propaga `PyodideUnavailableError` (Princípio X).
  // - Dev/test: fallback para mock (compat com suíte).
  try {
    // HLOS-335: chamar diretamente _js_process_turn_dict_impl (não js_process_turn_obj
    // que ainda passa por json.dumps). Esta função processa o dict DIRETAMENTE,
    // bypassando completamente o problema de serialização com dataUrl 4MB.
    const pyResult = await pyodide.runPythonAsync(
      `import hlos.kernel, gc; ` +
      `import js as __hlos_js; ` +
      `turn_obj = __hlos_js.window.__hlos_bridge.pop_turn(${JSON.stringify(turnId)}); ` +
      `gc.collect(); ` +
      `result = await hlos.kernel._js_process_turn_dict_impl(turn_obj, ${masterKeyArg}); ` +
      `del turn_obj; gc.collect(); ` +
      `result`,
    );
    // Python retorna string JSON (kernel protocol); parseamos para validar.
    return JSON.parse(String(pyResult)) as unknown;
  } catch (e) {
    // HLOS-debug-2026-09-08: REMOVIDO fallback mock silencioso completamente.
    // callKernel SEMPRE propaga erro (PyodideUnavailableError em prod,
    // Error em dev). O mock estava escondendo problemas reais.
    safeLogError("pyodide_failed", "pyodide_failed", String(e), e);
    throw e;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Validação (mantida para compat com testes de HLOS-7)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Valida o resultado retornado pelo kernel.
 * Em caso de violação constitucional (Princípio X + CC-05), retorna
 * R = -∞ silencioso: initiativeText = null, gaps vazio.
 *
 * HLOS-154 — também bloqueia ``reply`` que carrega representação de objetos
 * internos do Python (PyodideTask, coroutine, coroutine wrapper). Esses
 * textos vazariam detalhes de infraestrutura para o usuário
 * (Princípio XI, CC-06 — sem dark patterns / sem exposição de LLM).
 */
export function validateProcessTurnResult(raw: unknown): ProcessTurnResult {
  if (!isProcessTurnResult(raw)) {
    // Type guard mínimo — campos obrigatórios.
    const obj = raw as Partial<ProcessTurnResult>;
    let reply: string = "";
    if (typeof obj.reply === "string") {
      reply = looksLikeAwaitableRepr(obj.reply) ? "" : obj.reply;
    }
    return {
      reply,
      rLevel: "R3", // Conservador: silent
      initiativeText:
        typeof obj.initiativeText === "string" ? obj.initiativeText : null,
      curiosityGaps: Array.isArray(obj.curiosityGaps)
        ? obj.curiosityGaps.map(String).slice(0, 3)
        : [], // CL-12 cap
    };
  }
  // Mesmo payload já validado pelo type guard — checagem extra do reply.
  if (looksLikeAwaitableRepr(raw.reply)) {
    return {
      reply: "",
      rLevel: "R3",
      initiativeText: null,
      curiosityGaps: raw.curiosityGaps ?? [],
    };
  }
  return raw;
}

/**
 * HLOS-154 — detecta ``reply`` que é na verdade a repr de um objeto
 * ``asyncio.Task`` / ``PyodideTask`` / coroutine, em vez de texto do
 * Companheiro. Usado pelo validador para impedir que tais vazamentos
 * cheguem à UI do aprendiz (Princípio XI, CC-06).
 *
 * Esta função é deliberadamente conservadora: falsos positivos (tratar
 * texto legítimo como vazamento) resultam em ``reply = ""``, que o UI já
 * trata silenciosamente. Falsos negativos (deixar passar uma repr
 * inesperada) resultariam em vazamento constitucional.
 */
function looksLikeAwaitableRepr(text: string): boolean {
  if (!text) return false;
  // Padrões observados no incidente HLOS-154 e em casos análogos:
  //   "<PyodideTask pending name='Task-3' coro=<...> cb=[...]>"
  //   "<coroutine object WebLocalProvider.generate_async at 0x...>"
  //   "<Task pending coro=<...>>"
  return (
    text.includes("PyodideTask") ||
    text.includes("Task pending") ||
    text.includes("<coroutine") ||
    text.includes("coro=<") ||
    /<Task[^>]*pending/i.test(text)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mock (CI / test / dev sem Pyodide)
// ─────────────────────────────────────────────────────────────────────────────

function createMockPyodide(): PyodideLike {
  return {
    async runPythonAsync(code: string): Promise<unknown> {
      // Mock: extrai turnJson do code via regex e responde com mockKernelResponse.
      const match = code.match(/js_process_turn\("(.+)"\)/);
      const turnJson = match && match[1] ? match[1].replace(/\\"/g, '"') : "{}";
      const turn = JSON.parse(turnJson) as UserTurn;
      return JSON.stringify(mockKernelResponse(turn));
    },
    globals: {
      get(_name: string): unknown {
        return undefined;
      },
      set(_key: string, _value: unknown): void {
        // No-op: mock não persiste globals.
      },
    },
  };
}

function mockKernelResponse(turn: UserTurn): KernelResponse {
  const text = turn.text.toLowerCase();
  // Resposta constitucional mock: turn contendo "violar" (ou palavra-chave de teste)
  // → simula violação CC-05 (penalidade de autonomia, hipotética).
  if (text.includes("violate-cc05-test")) {
    return {
      kind: "constitutional_violation",
      violations: ["CC-05 (penalidade de autonomia detectada)"],
      cl_refs: ["CL-06", "CC-05"],
    };
  }
  // HLOS-cleanup-2026-09-08: removido throw incondicional do PR #244.
  // O Pyodide agora funciona corretamente (PR #245 CSP fix).
  // mockKernelResponse volta a ser apenas fallback de dev/test.
  // O guard `isProduction()` (PR #240) garante que em produção, este
  // mock NUNCA é chamado — o Pyodide real processa o turno.

  // Mock determinístico baseado no conteúdo do turno.
  if (text.includes("?")) {
    return {
      kind: "ok",
      result: {
        reply: `[MOCK ${Date.now()}] Anotado: "${turn.text.slice(0, 80)}${
          turn.text.length > 80 ? "…" : ""
        }".`,
        rLevel: "R3",
        initiativeText: null,
        curiosityGaps: [],
      },
    };
  }
  if (text.includes("explica") || text.includes("explique")) {
    return {
      kind: "ok",
      result: {
        reply: `[MOCK ${Date.now()}] Vou explicar isso.`,
        rLevel: "R1",
        initiativeText: "Quer que eu detalhe com um exemplo?",
        curiosityGaps: ["math"],
      },
    };
  }
  return {
    kind: "ok",
    result: {
      reply: `[MOCK ${Date.now()}] Anotado: "${turn.text.slice(0, 80)}${
        turn.text.length > 80 ? "…" : ""
      }".`,
      rLevel: "R3",
      initiativeText: null,
      curiosityGaps: [],
    },
  };
}

/** Re-export público do `Result<T, E>` para consumers. */
export type { Result };
